import React, { useState, useEffect, useCallback } from 'react';
import { useProject } from '../contexts/ProjectContext';
import { Action } from '../lib/auth';
import api from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Users,
  Wrench,
  Package,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Search,
  FolderKanban,
  AlertTriangle,
} from 'lucide-react';
import { apiErrorMessage } from '../lib/apiError';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

// Resource types as specified (Person, Equipment, Material only)
const RESOURCE_TYPES = [
  { value: 'person', label: 'Person', icon: Users, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'equipment', label: 'Equipment', icon: Wrench, color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { value: 'material', label: 'Material', icon: Package, color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
];

const getResourceTypeConfig = (type) => {
  return RESOURCE_TYPES.find(t => t.value === type?.toLowerCase()) || RESOURCE_TYPES[0];
};

const ResourcesPage = () => {
  const { activeSchedule, activeProject, can } = useProject();

  // State
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');

  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    resource_type: 'person',
    description: '',
  });

  // Fetch resources
  const fetchResources = useCallback(async () => {
    if (!activeSchedule?.id) return;

    setLoading(true);
    try {
      const response = await api.get(`/resources/schedule/${activeSchedule.id}`);
      setResources(response.data);
    } catch (error) {
      console.error('Failed to fetch resources:', error);
      toast.error('Failed to load resources');
    } finally {
      setLoading(false);
    }
  }, [activeSchedule?.id]);

  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  // Filter resources
  const filteredResources = resources.filter(resource => {
    const matchesSearch = !searchQuery || 
      resource.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || resource.resource_type === filterType;
    return matchesSearch && matchesType;
  });

  // Create resource
  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Resource name is required');
      return;
    }

    setSaving(true);
    try {
      await api.post('/resources', {
        ...formData,
        schedule_id: activeSchedule.id,
      });
      toast.success('Resource created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchResources();
    } catch (error) {
      console.error('Failed to create resource:', error);
      toast.error(apiErrorMessage(error, 'Failed to create resource'));
    } finally {
      setSaving(false);
    }
  };

  // Edit resource
  const handleEdit = async () => {
    if (!formData.name.trim()) {
      toast.error('Resource name is required');
      return;
    }

    setSaving(true);
    try {
      await api.put(`/resources/${selectedResource.id}`, formData);
      toast.success('Resource updated successfully');
      setShowEditDialog(false);
      resetForm();
      fetchResources();
    } catch (error) {
      console.error('Failed to update resource:', error);
      toast.error(apiErrorMessage(error, 'Failed to update resource'));
    } finally {
      setSaving(false);
    }
  };

  // Delete resource
  const handleDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/resources/${selectedResource.id}`);
      toast.success('Resource deleted successfully');
      setShowDeleteDialog(false);
      setSelectedResource(null);
      fetchResources();
    } catch (error) {
      console.error('Failed to delete resource:', error);
      toast.error(apiErrorMessage(error, 'Failed to delete resource'));
    } finally {
      setSaving(false);
    }
  };

  // Open edit dialog
  const openEditDialog = (resource) => {
    setSelectedResource(resource);
    setFormData({
      name: resource.name,
      resource_type: resource.resource_type,
      description: resource.description || '',
    });
    setShowEditDialog(true);
  };

  // Open delete dialog
  const openDeleteDialog = (resource) => {
    setSelectedResource(resource);
    setShowDeleteDialog(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      resource_type: 'person',
      description: '',
    });
    setSelectedResource(null);
  };

  // Count by type
  const typeCounts = RESOURCE_TYPES.map(type => ({
    ...type,
    count: resources.filter(r => r.resource_type === type.value).length
  }));

  if (!activeProject || !activeSchedule) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3">
              <FolderKanban className="h-6 w-6 text-slate-400" />
            </div>
            <CardTitle className="text-lg">No Project Selected</CardTitle>
            <CardDescription>
              Select a project and schedule to manage resources
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-slate-900">Resources</h1>
            <p className="text-sm text-slate-500 mt-0.5 truncate">
              Manage resources for {activeSchedule?.name || 'your schedule'}
            </p>
          </div>
          {can(Action.MANAGE_SCHEDULES) && (
            <Button
              onClick={() => {
                resetForm();
                setShowCreateDialog(true);
              }}
              className="gap-2"
              data-testid="create-resource-btn"
            >
              <Plus className="h-4 w-4" />
              New Resource
            </Button>
          )}
        </div>

        {/* Type Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mt-4">
          {typeCounts.map((type) => {
            const Icon = type.icon;
            return (
              <div
                key={type.value}
                onClick={() => setFilterType(filterType === type.value ? 'all' : type.value)}
                className={cn(
                  "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all",
                  filterType === type.value 
                    ? "border-slate-900 bg-slate-50" 
                    : "border-slate-200 bg-white hover:border-slate-300"
                )}
              >
                <div className={cn("p-2 rounded-lg shrink-0", type.color.split(' ')[0])}>
                  <Icon className={cn("h-4 w-4", type.color.split(' ')[1])} />
                </div>
                <div className="min-w-0">
                  <div className="text-lg font-semibold text-slate-900">{type.count}</div>
                  <div className="text-xs text-slate-500 truncate">{type.label}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white border-b border-slate-200 px-6 py-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
          <div className="relative w-full sm:flex-1 sm:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search resources..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-resources"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-full sm:w-48" data-testid="filter-type">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {RESOURCE_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Resources Table */}
      <div className="flex-1 overflow-hidden p-6">
        <Card className="h-full flex flex-col">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              {filterType === 'all' ? 'All Resources' : getResourceTypeConfig(filterType).label}
              <span className="text-slate-400 font-normal ml-2">
                ({filteredResources.length})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : filteredResources.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Package className="h-12 w-12 text-slate-200 mb-3" />
                <p className="text-slate-600 font-medium">No resources found</p>
                <p className="text-sm text-slate-400 mt-1">
                  {searchQuery || filterType !== 'all' 
                    ? 'Try adjusting your search or filter'
                    : 'Create your first resource to get started'}
                </p>
                {can(Action.MANAGE_SCHEDULES) && !searchQuery && filterType === 'all' && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={() => {
                      resetForm();
                      setShowCreateDialog(true);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create Resource
                  </Button>
                )}
              </div>
            ) : (
              <div className="h-full overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[200px]">Name</TableHead>
                      <TableHead className="w-[140px]">Type</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="w-[100px] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredResources.map((resource) => {
                      const typeConfig = getResourceTypeConfig(resource.resource_type);
                      const Icon = typeConfig.icon;
                      return (
                        <TableRow key={resource.id} data-testid={`resource-row-${resource.id}`}>
                          <TableCell className="font-medium">
                            {resource.name}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={cn("gap-1.5", typeConfig.color)}>
                              <Icon className="h-3 w-3" />
                              {typeConfig.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-500 max-w-[300px] truncate">
                            {resource.description || '—'}
                          </TableCell>
                          <TableCell className="text-right">
                            {can(Action.MANAGE_SCHEDULES) && (
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditDialog(resource)}
                                  data-testid={`edit-resource-${resource.id}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openDeleteDialog(resource)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`delete-resource-${resource.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Create Resource</DialogTitle>
            <DialogDescription>
              Add a new resource to your schedule. Resources can be assigned to activities.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Resource Name *</Label>
              <Input
                id="name"
                placeholder="Enter resource name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="resource-name-input"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="type">Resource Type</Label>
              <Select
                value={formData.resource_type}
                onValueChange={(value) => setFormData({ ...formData, resource_type: value })}
              >
                <SelectTrigger data-testid="resource-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {RESOURCE_TYPES.map(type => {
                    const Icon = type.icon;
                    return (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" />
                          {type.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                placeholder="Enter description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                data-testid="resource-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="save-resource-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Resource
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit Resource</DialogTitle>
            <DialogDescription>
              Update the resource details.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-name">Resource Name *</Label>
              <Input
                id="edit-name"
                placeholder="Enter resource name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-type">Resource Type</Label>
              <Select
                value={formData.resource_type}
                onValueChange={(value) => setFormData({ ...formData, resource_type: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {RESOURCE_TYPES.map(type => {
                    const Icon = type.icon;
                    return (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" />
                          {type.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-description">Description (Optional)</Label>
              <Textarea
                id="edit-description"
                placeholder="Enter description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleEdit} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Delete Resource
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{selectedResource?.name}</strong>?
              This will also remove all activity assignments for this resource.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={saving}
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ResourcesPage;
